import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, moneyAmount } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = { loading: false, error: null, notice: '' };

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function coreSnapshot() {
  const core = useCoreSession.getState().state || {};
  const inventory = Array.isArray(core.inventory) ? core.inventory : [];
  const capacity = Number(core.inventoryCapacity || 12);
  const used = inventory.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  return { core, inventory, capacity, used };
}

function drawTop(ctx, width) {
  const session = useCoreSession.getState();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // COAT', 8, 5, TERMINAL.TEXT);
  if (width >= 500) text(ctx, 'TOMMY // TAILOR', 216, 5, TERMINAL.TEXT_DIM);
  text(ctx, session.status === 'online' ? 'LIVE' : 'OFFLINE', width - 64, 5, session.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawSlots(ctx, x, y, w, h) {
  const { inventory, capacity, used } = coreSnapshot();
  DopeUI.Panel(ctx, x, y, w, h, `CAPACITY // ${used}/${capacity}`);
  const cols = 4;
  const cellW = Math.min(76, Math.floor((w - 32 - 24) / 4));
  const cellH = 44;
  const gap = 8;
  const slots = [];
  inventory.forEach((item) => {
    for (let i = 0; i < Number(item.quantity || 0); i += 1) slots.push(item);
  });
  const visible = Math.min(capacity, 12);
  for (let i = 0; i < visible; i += 1) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = x + 12 + col * (cellW + gap);
    const cy = y + 34 + row * (cellH + gap);
    const item = slots[i];
    ctx.fillStyle = TERMINAL.BLACK;
    ctx.fillRect(cx, cy, cellW, cellH);
    ctx.strokeStyle = item ? TERMINAL.GREEN : TERMINAL.GREEN_DARK;
    ctx.strokeRect(cx, cy, cellW, cellH);
    text(ctx, `SLOT ${String(i + 1).padStart(2, '0')}`, cx + 6, cy + 6, TERMINAL.TEXT_DIM);
    text(ctx, item ? String(item.assetId).slice(0, 8).toUpperCase() : '--', cx + 6, cy + 24, item ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN_DARK);
  }
  if (capacity > visible) {
    text(ctx, `+${capacity - visible} SLOTS HIDDEN // COAT CAPACITY`, x + 12, y + h - 22, TERMINAL.TEXT_DIM);
  } else {
    text(ctx, state.error || state.notice || 'INVENTORY LIVE', x + 12, y + h - 22, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
  }
}

function drawTerms(ctx, x, y, w, h) {
  const { cap } = { cap: coreSnapshot().capacity };
  const core = coreSnapshot().core;
  const cash = moneyAmount(core.cash);
  DopeUI.Panel(ctx, x, y, w, h, 'TAILOR // TERMS');
  text(ctx, 'STANDARD', x + 12, y + 30, TERMINAL.TEXT_DIM);
  text(ctx, '12 SLOTS', x + 128, y + 30, cap === 12 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
  text(ctx, 'REINFORCED', x + 12, y + 56, TERMINAL.TEXT_DIM);
  text(ctx, '30 SLOTS', x + 128, y + 56, cap === 30 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
  text(ctx, '$300', x + 12, y + 76, TERMINAL.TEXT);
  text(ctx, 'SMUGGLER', x + 12, y + 102, TERMINAL.TEXT_DIM);
  text(ctx, '60 SLOTS', x + 128, y + 102, cap === 60 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
  text(ctx, '$750', x + 12, y + 122, TERMINAL.TEXT);
  rule(ctx, x + 12, y + 150, w - 24);
  text(ctx, `HRONIR RESERVE  04`, x + 12, y + 164, TERMINAL.TEXT_DIM);
  text(ctx, `CASH            $${cash.toFixed(2)}`, x + 12, y + 182, TERMINAL.TEXT_DIM);

  const nextCap = cap === 12 ? 30 : cap === 30 ? 60 : null;
  const online = useCoreSession.getState().status === 'online';
  const canExpand = online && nextCap && !state.loading;
  DopeUI.Button(ctx, x + 12, y + h - 42, w - 24, 26, nextCap ? `EXPAND TO ${nextCap}` : 'MAX CAPACITY', canExpand ? 'hover' : 'disabled');
  if (canExpand) {
    interactiveRegistry.register({
      id: 'coat-expand',
      x: x + 12,
      y: y + h - 42,
      w: w - 24,
      h: 26,
      onClick: async () => {
        state.loading = true;
        state.error = null;
        try {
          const result = await useCoreSession.getState().expandCoat();
          state.notice = `CAPACITY ${result.coat?.capacityAfter ?? nextCap}`;
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
}

registerView('coat', function renderCoat(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  drawTop(ctx, safe.width);
  const mobile = safe.width < 500;
  if (mobile) {
    drawSlots(ctx, 8, 32, safe.width - 16, 286);
    drawTerms(ctx, 8, 326, safe.width - 16, 240);
  } else {
    drawSlots(ctx, 8, 36, 376, 280);
    drawTerms(ctx, 400, 36, 232, 280);
  }

  const footerY = mobile ? 578 : 328;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TAB FOCUS   ENTER   ESC DESKTOP', 8, mobile ? footerY + 8 : 360, TERMINAL.TEXT_DIM);
  const backX = mobile ? 8 : safe.width - 200;
  const backY = mobile ? 604 : 336;
  const backW = mobile ? safe.width - 16 : 192;
  DopeUI.Button(ctx, backX, backY, backW, 24, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'coat-back', x: backX, y: backY, w: backW, h: 24,
    onClick: () => navigateTo('main_menu'),
  });
  if (!mobile) text(ctx, 'COAT // CAPACITY IS A CASH SINK, NOT A SETTING.', 8, 384, TERMINAL.GREEN);
});
